import * as React from 'react';
import styled from 'styled-components';
import { useRouter } from 'next/router';
import Link from 'next/link';
import { media, themeGet, is } from '@packages/ui';
import { useFastlane } from '@packages/shared';
import { FlyoutTileItem, isTouchDevice, CategoryLinkItem } from '@packages/common';
import { getPublicRuntimeConfig } from '@helpers/getPublicRuntimeConfig';

import { Flyout, LinkItem } from '../../Navigation';
import { getInspirationItems } from '../../../constants';
import flyoutConfig from '../../../layoutConfigs/flyout.json';
import mainNavigation from '../../../layoutConfigs/main_navigation.json';

const Wrapper = styled.div`
  position: relative;
  z-index: ${themeGet.zIndex('MIDDLE', 1)};
  ${media.to.lg`
    display:none;
  `};
`;

const MainNavWrapper = styled.nav`
  box-shadow: 0px 8px 8px -5px rgba(43, 18, 2, 0.3);
  position: relative;
`;

const FlyoutWrapper = styled.div<{ isOpen: boolean }>`
  position: absolute;
  z-index: 1;
  width: 100%;
  box-shadow: 0px 8px 8px -5px rgba(43, 18, 2, 0.3);
  height: 0px;
  top: 100%;
  background-color: ${themeGet.colors('white')};
  border-radius: 0 0 6px 6px;
  transition: ${themeGet.transition('height', 'fast')};
  transition-delay: 0;

  ${is('isOpen')`
    transition-delay: 0.4s;
    height: 420px;
  `};
`;

const MainNavItems = styled.ul`
  display: flex;
  flex-flow: row wrap;
  justify-content: center;
  margin: 0;
  padding: 0;
  list-style: none;
  background-color: ${themeGet.colors('potPourri')};
  border-radius: 0 0 6px 6px;
`;

const getFirstCategoryFromUrl = (url: string) => `/${url.split('/').filter((item) => item !== '')[0]}`;

const APP_TLD = getPublicRuntimeConfig('APP_TLD');

const getFlyoutTilesConfig = (categoryKey: string) => {
  const activeFlyoutConfig = (flyoutConfig as any)[categoryKey];
  return activeFlyoutConfig?.content ? activeFlyoutConfig.content[0]?.props?.items : undefined;
};

export const MainNavigation: React.FC = () => {
  const fastlaneClient = useFastlane();

  const { asPath } = useRouter();
  const [mainCategoryId, setMainCategoryId] = React.useState(getFirstCategoryFromUrl(asPath));
  const [activeCategoryId, setActiveCategoryId] = React.useState('');
  const [flyoutTileItems, setFlyoutTileItems] = React.useState([] as FlyoutTileItem[]);
  const [allowCategoryNavigation, setAllowCategoryNavigation] = React.useState(!isTouchDevice());

  const flyoutActive = activeCategoryId !== '';

  React.useEffect(() => {
    setMainCategoryId(getFirstCategoryFromUrl(asPath));
    setActiveCategoryId('');
  }, [asPath, setMainCategoryId, setActiveCategoryId]);

  React.useEffect(() => {
    if (flyoutActive) {
      const activeFlyoutTileItems = getFlyoutTilesConfig(activeCategoryId);
      setFlyoutTileItems(activeFlyoutTileItems);
    }
  }, [activeCategoryId, flyoutActive, fastlaneClient]);

  const handleCloseFlyout = () => {
    setActiveCategoryId('');
  };

  const handleItemHover = (item: any) => () => {
    setActiveCategoryId(item.url);
  };

  const handleMainItemClick = (item: CategoryLinkItem) => () => {
    if (isTouchDevice()) {
      return;
    }

    if (item.url === activeCategoryId) {
      handleCloseFlyout();
    }
  };

  const handleMainItemTouch = (item: CategoryLinkItem) => () => {
    if (activeCategoryId === item.url) {
      handleCloseFlyout();
      setAllowCategoryNavigation(true);
    } else {
      // set active category id here, so a user can open the flyout on a
      // touch-device that is currently active (but the flyout is closed)
      setActiveCategoryId(item.url);
      setAllowCategoryNavigation(false);
    }
  };

  const handleCategoryNavigate = (e: React.MouseEvent) => {
    // prevent first click, because it should be treated like `hover` on
    // pointer-devices (open flyout before navigating)
    if (isTouchDevice() && !allowCategoryNavigation) {
      e.preventDefault();
    }
  };

  const inspirationItems = getInspirationItems(APP_TLD);
  const { main, flyout } = mainNavigation;
  return main ? (
    <Wrapper onMouseLeave={handleCloseFlyout}>
      <MainNavWrapper>
        <MainNavItems>
          {main.map((item) => (
            <LinkItem
              key={item.title}
              isActive={item.url === activeCategoryId}
              isCurrent={item.url === mainCategoryId}
              onHover={handleItemHover(item)}
              onClick={handleMainItemClick(item)}
              onTouch={handleMainItemTouch(item)}
            >
              <Link prefetch={false} href={item.url}>
                <a href={item.url} onClick={handleCategoryNavigate}>
                  {item.shortTitle}
                </a>
              </Link>
            </LinkItem>
          ))}
          {inspirationItems
            .filter((item) => !item.mobileOnly)
            .map((item) => (
              <LinkItem
                key={item.title}
                isActive={item.as === activeCategoryId}
                isCurrent={item.as === mainCategoryId}
                emphesize={item.emphesize}
              >
                <Link href={item.as} prefetch={false}>
                  <a>{item.title}</a>
                </Link>
              </LinkItem>
            ))}
        </MainNavItems>
      </MainNavWrapper>
      <FlyoutWrapper isOpen={flyoutActive} data-testid="flyout-wrapper">
        <Flyout
          items={(flyout as any)[activeCategoryId]}
          tiles={flyoutTileItems}
          handleCloseFlyout={handleCloseFlyout}
        />
      </FlyoutWrapper>
    </Wrapper>
  ) : (
    <></>
  );
};
